import type {
  CreateMapNoteTemplateInput,
  CreateMapNoteTemplateEntryInput,
  MapNoteTemplate,
  MapNoteTemplateEntry,
  MapNoteTemplateSummary,
  UpdateMapNoteTemplateInput,
  UpdateMapNoteTemplateEntryInput,
} from "@west-marches/shared";

import { apiFetch } from "@/lib/apiClient";

async function readJson<T>(response: Response): Promise<T> {
  const payload = (await response.json().catch(() => ({}))) as T & {
    error?: string;
  };
  if (!response.ok) {
    const message =
      typeof payload.error === "string" && payload.error ? payload.error : "Request failed";
    throw new Error(message);
  }
  return payload;
}

export async function listMapNoteTemplates(): Promise<MapNoteTemplateSummary[]> {
  const response = await apiFetch("/api/map-note-templates", { method: "GET" });
  const payload = await readJson<{ templates: MapNoteTemplateSummary[] }>(response);
  return payload.templates;
}

export async function getMapNoteTemplate(templateId: string): Promise<MapNoteTemplate> {
  const response = await apiFetch(
    `/api/map-note-templates/${encodeURIComponent(templateId)}`,
    { method: "GET" },
  );
  const payload = await readJson<{ template: MapNoteTemplate }>(response);
  return payload.template;
}

export async function createMapNoteTemplate(
  input: CreateMapNoteTemplateInput,
): Promise<MapNoteTemplateSummary> {
  const response = await apiFetch("/api/map-note-templates", {
    method: "POST",
    body: JSON.stringify(input),
  });
  const payload = await readJson<{ template: MapNoteTemplateSummary }>(response);
  return payload.template;
}

export async function updateMapNoteTemplate(
  templateId: string,
  input: UpdateMapNoteTemplateInput,
): Promise<MapNoteTemplateSummary> {
  const response = await apiFetch(
    `/api/map-note-templates/${encodeURIComponent(templateId)}`,
    { method: "PATCH", body: JSON.stringify(input) },
  );
  const payload = await readJson<{ template: MapNoteTemplateSummary }>(response);
  return payload.template;
}

export async function deleteMapNoteTemplate(templateId: string): Promise<void> {
  const response = await apiFetch(
    `/api/map-note-templates/${encodeURIComponent(templateId)}`,
    { method: "DELETE" },
  );
  await readJson<{ ok: boolean }>(response);
}

export async function createTemplateFromMapNote(
  campaignId: string,
  mapNoteId: string,
  name: string,
): Promise<MapNoteTemplateSummary> {
  const response = await apiFetch("/api/map-note-templates/from-map-note", {
    method: "POST",
    body: JSON.stringify({ campaignId, mapNoteId, name }),
  });
  const payload = await readJson<{ template: MapNoteTemplateSummary }>(response);
  return payload.template;
}

export async function listMapNoteTemplateEntries(
  templateId: string,
): Promise<MapNoteTemplateEntry[]> {
  const response = await apiFetch(
    `/api/map-note-templates/${encodeURIComponent(templateId)}/entries`,
    { method: "GET" },
  );
  const payload = await readJson<{ entries: MapNoteTemplateEntry[] }>(response);
  return payload.entries;
}

export async function createMapNoteTemplateEntry(
  templateId: string,
  input: CreateMapNoteTemplateEntryInput,
): Promise<MapNoteTemplateEntry> {
  const response = await apiFetch(
    `/api/map-note-templates/${encodeURIComponent(templateId)}/entries`,
    { method: "POST", body: JSON.stringify(input) },
  );
  const payload = await readJson<{ entry: MapNoteTemplateEntry }>(response);
  return payload.entry;
}

export async function updateMapNoteTemplateEntry(
  templateId: string,
  entryId: string,
  input: UpdateMapNoteTemplateEntryInput,
): Promise<MapNoteTemplateEntry> {
  const response = await apiFetch(
    `/api/map-note-templates/${encodeURIComponent(templateId)}/entries/${encodeURIComponent(entryId)}`,
    { method: "PATCH", body: JSON.stringify(input) },
  );
  const payload = await readJson<{ entry: MapNoteTemplateEntry }>(response);
  return payload.entry;
}

export async function deleteMapNoteTemplateEntry(
  templateId: string,
  entryId: string,
): Promise<void> {
  const response = await apiFetch(
    `/api/map-note-templates/${encodeURIComponent(templateId)}/entries/${encodeURIComponent(entryId)}`,
    { method: "DELETE" },
  );
  await readJson<{ ok: boolean }>(response);
}
